import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getHasProductVersionInProductInventory, getProductsFromProductInventoryQuantity } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getCurrentSkuIdForProduct, getIsCoreSeatSku, getProductForSkuId, isQuantifiableHub } from 'pricing-pages-lib/utils/productsUtils';
import { useCallback, useContext, useId } from 'react';
import { shallowEqual } from 'react-redux';
import { currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import styled from 'styled-components';
import UICardHeader from 'UIComponents/card/UICardHeader';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import { PlanBuilderContext } from '../../../context/PlanBuilderContext';
import { useAppSelector } from '../../../reducers';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { productVersionsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { currentProductVersionSelector, currentSkuIdForProductSelector, isSalesOrServiceProOrEntSkuSelector, productForSkuIdSelector, productVersionSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { gatesSelector, productDataSelector, productInventorySelector, usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { productNamesByProductLevelMapSelector } from '../../../selectors/TrackingSelectors';
import { seleniumUnitQuantitiesStringSelector, seleniumUnitsTypesStringSelector } from '../../../utils/planBuilderUtils/SeleniumUtils';
import { SectionTitle } from '../../common/SharedComponents';
import QuantityPacksPickerContainer from '../QuantityPacksPickerContainer';
import HubProductLevelPicker from './HubProductLevelPicker';
import PlanPickerHeader from './PlanPickerHeader';
const StyledPlanPickerWrapper = styled.div.withConfig({
  displayName: "HubPlanPickerList__StyledPlanPickerWrapper",
  componentId: "sc-1srbl92-0"
})(["display:flex;flex-direction:column;flex:1;width:inherit;"]);
const Container = styled(UICardWrapper).withConfig({
  displayName: "HubPlanPickerList__Container",
  componentId: "sc-1srbl92-1"
})(["max-width:700px;"]);
const StyledCardHeader = styled(UICardHeader).withConfig({
  displayName: "HubPlanPickerList__StyledCardHeader",
  componentId: "sc-1srbl92-2"
})(["& > div > span{padding-bottom:0;}"]);

const PlanPicker = ({
  productLine,
  className,
  compact
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const {
    productConfigurations,
    setProductConfigurations,
    requiredProductLines,
    trackingSource,
    screen
  } = useContext(PlanBuilderContext);
  const currencyCode = useAppSelector(activeCurrencySelector);
  const required = requiredProductLines.includes(productLine);
  const ownedSkuId = useAppSelector(state => {
    const ownedProductVersion = productVersionsFromProductInventorySelector(state).find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    return ownedProductVersion && ownedProductVersion.skuId;
  });
  const ownedCurrentSkuId = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      productLevel: ownedProductLevel,
      productLine: ownedProductLine
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const {
      skuId: currentProductVersionSkuId
    } = currentProductVersionSelector(state, {
      productLevel: ownedProductLevel,
      productLine: ownedProductLine
    });
    return ownedSkuId === currentProductVersionSkuId ? ownedSkuId : null;
  });
  const selectedSkuId = useAppSelector(state => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    let updatedOwnedSkuId;

    if (ownedCurrentSkuId) {
      const {
        productId
      } = skuSelector(state, {
        skuId: ownedCurrentSkuId
      });
      updatedOwnedSkuId = ownedCurrentSkuId && currentSkuIdForProductSelector(state, {
        productId
      });
    }

    return foundConfig ? foundConfig.skuId : updatedOwnedSkuId;
  });
  const ownedProductLevel = useAppSelector(state => ownedCurrentSkuId ? productForSkuIdSelector(state, {
    skuId: ownedCurrentSkuId
  }).productLevel : null); // This can be different than ownedProductLevel if you own a legacy product (sidegrades)

  const disabledProductLevel = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      productLevel
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const currentOrderedPaidProductLevel = productLevel === currentOrderedPaidProductLevels[0] ? null : currentOrderedPaidProductLevels[currentOrderedPaidProductLevels.indexOf(productLevel) - 1];
    return currentOrderedPaidProductLevel;
  });
  const selectedProductLevel = useAppSelector(state => selectedSkuId ? productForSkuIdSelector(state, {
    skuId: selectedSkuId
  }).productLevel : null);
  const trackingProductNamesByProductLevelMap = useAppSelector(state => productNamesByProductLevelMapSelector(state, {
    productLine
  }), shallowEqual);
  const productData = useAppSelector(productDataSelector);
  const gates = useAppSelector(gatesSelector);
  const productInventory = useAppSelector(productInventorySelector);
  const setProductLevel = useCallback(newProductLevel => {
    if (!newProductLevel) {
      if (selectedProductLevel === ownedProductLevel) {
        return;
      } else {
        newProductLevel = ownedProductLevel;
      }
    }

    if (newProductLevel) {
      getUsageTracker().track('planBuilderInteraction', {
        component: 'HubPlanPickerList',
        action: 'clicked product level selection button',
        productName: trackingProductNamesByProductLevelMap[newProductLevel],
        skuId: getCurrentSkuIdForProduct({
          productData,
          gates
        }, {
          productLine,
          productLevel: newProductLevel
        }),
        screen,
        currency: currencyCode,
        trackingSource
      });
    }

    setProductConfigurations(prevConfigs => {
      const filteredConfigs = prevConfigs.filter(({
        skuId
      }) => {
        const product = getProductForSkuId({
          productData
        }, {
          skuId
        });
        const isCoreSeat = getIsCoreSeatSku({
          productData
        }, {
          skuId
        });
        return !isCoreSeat && (!product.productLine || product.productLine !== productLine || !product.productLevel);
      });

      if (newProductLevel) {
        const newSkuId = getCurrentSkuIdForProduct({
          productData,
          gates
        }, {
          productLine,
          productLevel: newProductLevel
        });

        if (newSkuId && (!getHasProductVersionInProductInventory({
          productInventory,
          productData
        }, {
          skuId: newSkuId
        }) || isQuantifiableHub({
          productData
        }, {
          skuId: newSkuId
        }))) {
          const ownedQuantity = getProductsFromProductInventoryQuantity({
            productInventory,
            productData
          }, {
            skuId: newSkuId
          });
          filteredConfigs.push(createProductConfig({
            skuId: newSkuId,
            quantity: ownedQuantity || 1
          }));
        }
      }

      return filteredConfigs;
    });
  }, [productData, productLine, setProductConfigurations]);
  const additionalUnitSkuIds = useAppSelector(state => {
    let skuIds = [];

    if (selectedSkuId) {
      const {
        quantifiable: isSkuQuantifiable
      } = skuSelector(state, {
        skuId: selectedSkuId
      });
      const isSalesOrServiceProOrEntSku = isSalesOrServiceProOrEntSkuSelector(state, {
        skuId: selectedSkuId
      });
      const quantityPackDetails = productVersionSelector(state, {
        skuId: selectedSkuId
      }).quantityPackDetails; // if in seats pilot, the sku is quantifiable and is sales/service pro/ent

      if (isSkuQuantifiable && isSalesOrServiceProOrEntSku && isSeatsRedesignUngated) {
        // add the sku as an additional unit
        skuIds = [selectedSkuId, ...skuIds]; // else check for any quantity packs to add as additional units
      } else if (quantityPackDetails.length > 0) {
        skuIds = [...quantityPackDetails.map(({
          skuId
        }) => {
          return skuId;
        }), ...skuIds];
      }
    }

    return skuIds;
  });
  const seleniumUnitsString = useAppSelector(state => seleniumUnitQuantitiesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const seleniumUnitsTypesIdString = useAppSelector(state => seleniumUnitsTypesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const apiName = useAppSelector(state => {
    const product = selectedSkuId && productForSkuIdSelector(state, {
      skuId: selectedSkuId
    });
    return product && product.apiName;
  });
  const hubProductLevelPickerLabelId = useId();
  const Wrapper = compact ? 'div' : Container;
  const SectionContainer = compact ? 'div' : UICardSection;

  const PlanPickerTitle = /*#__PURE__*/_jsx(PlanPickerHeader, {
    productLine: productLine,
    compact: compact // trackingSource={trackingSource}
    ,
    id: hubProductLevelPickerLabelId
  });

  return /*#__PURE__*/_jsxs(Wrapper, {
    className: className,
    "data-product-level": selectedProductLevel,
    "data-selenium-id": `${productLine}-plan-builder-picker`,
    "data-api-name": apiName,
    "data-units": seleniumUnitsString || '0',
    "data-units-type": seleniumUnitsTypesIdString,
    children: [compact ? /*#__PURE__*/_jsx("div", {
      children: PlanPickerTitle
    }) : /*#__PURE__*/_jsx(StyledCardHeader, {
      titleSize: "xs",
      title: PlanPickerTitle
    }), /*#__PURE__*/_jsxs(SectionContainer, {
      children: [/*#__PURE__*/_jsx(HubProductLevelPicker, {
        ariaLabelledById: hubProductLevelPickerLabelId,
        productLine: productLine,
        required: required,
        selectedProductLevel: selectedProductLevel || null,
        ownedProductLevel: ownedProductLevel,
        disabledProductLevel: disabledProductLevel,
        setSelectedProductLevel: setProductLevel
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: additionalUnitSkuIds.length > 0,
        children: /*#__PURE__*/_jsx(QuantityPacksPickerContainer, {
          additionalUnitSkuIds: additionalUnitSkuIds,
          setProductLevel: setProductLevel,
          trackingSource: trackingSource,
          showEndDivider: compact
        })
      })]
    })]
  });
};

const HubPlanPickerList = ({
  productLines,
  compact = false
}) => {
  return /*#__PURE__*/_jsxs(StyledPlanPickerWrapper, {
    children: [!compact && /*#__PURE__*/_jsx(SectionTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "planBuilder.selectProductsAndAddons"
      })
    }), productLines.map(productLine => /*#__PURE__*/_jsx(PlanPicker, {
      productLine: productLine,
      compact: compact
    }, productLine))]
  });
};

export default HubPlanPickerList;